import { ISaSmsTenantDetail } from '@interfaces/sa-sms';
import { Button, Card, Descriptions, Space, Steps, Typography } from 'antd';
import { notification } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SaSmsReasonModal } from './sa-sms-reason-modal';
import { NumberBillingTag, RegistrationTag } from './sms-status-tags';

const FLOW = ['draft', 'submitted', 'brand_pending', 'campaign_pending', 'approved'];
type Action = 'repoll' | 'suspend' | 'unsuspend' | 'release-number';

type Props = { detail: ISaSmsTenantDetail; canEdit: boolean; onChanged: () => void };

/** Sender / A2P progress, number and number billing, with re-poll / suspend / unsuspend / release-now (reason required). */
export const SmsSenderTimeline: React.FC<Props> = ({ detail, canEdit, onChanged }) => {
    const { t, i18n } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const [action, setAction] = useState<Action | null>(null);
    const sender = detail.sender;
    const status = sender?.registrationStatus ?? 'not_started';
    const failed = status === 'rejected' || status === 'suspended';
    const current = Math.max(FLOW.indexOf(status), 0);

    const path = (a: Action) => (a === 'release-number' ? 'release-number' : `sender/${a}`);
    const confirm = async ({ reason }: { reason: string }) => {
        if (!action) return;
        await send('post', `/tenants/${detail.tenantId}/${path(action)}`, { reason });
        notification.success({ message: t('saved') });
        setAction(null);
        onChanged();
    };

    if (!sender) {
        return (
            <Card title={t('detail.sender')}>
                <Typography.Text type="secondary">{t('sender.none')}</Typography.Text>
            </Card>
        );
    }

    return (
        <Card
            title={t('detail.sender')}
            extra={
                canEdit && (
                    <Space wrap>
                        <Button onClick={() => setAction('repoll')}>{t('sender.repoll')}</Button>
                        {status === 'suspended' ? (
                            <Button onClick={() => setAction('unsuspend')}>
                                {t('sender.unsuspend')}
                            </Button>
                        ) : (
                            <Button danger onClick={() => setAction('suspend')}>
                                {t('sender.suspend')}
                            </Button>
                        )}
                        {sender.numberStatus === 'active' && (
                            <Button danger onClick={() => setAction('release-number')}>
                                {t('sender.release')}
                            </Button>
                        )}
                    </Space>
                )
            }
        >
            <Steps
                size="small"
                current={current}
                status={failed ? 'error' : status === 'approved' ? 'finish' : 'process'}
                items={FLOW.map((s) => ({ title: t(`status.${s}`) }))}
                style={{ marginBottom: 16 }}
            />
            <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
                <Descriptions.Item label={t('sender.status')}>
                    <RegistrationTag status={status} />
                </Descriptions.Item>
                <Descriptions.Item label={t('sender.numberStatus')}>
                    {sender.numberStatus
                        ? t(`numberStatus.${sender.numberStatus}`, {
                              defaultValue: sender.numberStatus,
                          })
                        : '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('list.number')}>
                    {sender.phoneMasked ?? '-'}{' '}
                    {sender.numberType &&
                        `(${t(`numberType.${sender.numberType}`, {
                            defaultValue: sender.numberType,
                        })})`}
                </Descriptions.Item>
                <Descriptions.Item label={t('list.subaccount')}>
                    <Typography.Text code>{sender.subaccountSid ?? '-'}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t('list.numberBilling')}>
                    <NumberBillingTag status={detail.numberBilling} />
                </Descriptions.Item>
                <Descriptions.Item label={t('sender.updatedAt')}>
                    {sender.updatedAt
                        ? new Date(sender.updatedAt).toLocaleString(i18n.language)
                        : '-'}
                </Descriptions.Item>
            </Descriptions>
            <SaSmsReasonModal
                open={!!action}
                danger={action === 'suspend' || action === 'release-number'}
                title={action ? t(`sender.confirm.${action}`) : ''}
                warning={action === 'release-number' ? t('sender.releaseWarning') : undefined}
                onCancel={() => setAction(null)}
                onConfirm={confirm}
            />
        </Card>
    );
};
